import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get token from multiple sources
const getStoredToken = () => {
  try {
    // Try localStorage first
    const localToken = localStorage.getItem('session_token');
    if (localToken) return localToken;
    
    // Try sessionStorage as backup
    const sessionToken = sessionStorage.getItem('session_token');
    if (sessionToken) return sessionToken;
  } catch (e) {
    console.warn('Storage access error:', e);
  }
  return null;
};

// Helper to store token in multiple locations
const storeToken = (token) => {
  try {
    localStorage.setItem('session_token', token);
  } catch (e) {
    console.warn('localStorage error:', e);
  }
  try {
    sessionStorage.setItem('session_token', token);
  } catch (e) {
    console.warn('sessionStorage error:', e);
  }
};

// Helper to clear token from all locations
const clearToken = () => {
  try {
    localStorage.removeItem('session_token');
  } catch (e) {}
  try {
    sessionStorage.removeItem('session_token');
  } catch (e) {}
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(null);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Check for stored token from multiple sources
    const storedToken = getStoredToken();
    
    try {
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // ALWAYS send Authorization header if we have a token (critical for iOS)
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
      
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers,
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        if (storedToken) {
          setSessionToken(storedToken);
        }
      } else {
        setUser(null);
        setSessionToken(null);
        clearToken();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData, token) => {
    setUser(userData);
    if (token) {
      setSessionToken(token);
      storeToken(token);
    }
  };

  const logout = async () => {
    const storedToken = getStoredToken();
    try {
      const headers = {};
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
      
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers,
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setSessionToken(null);
    clearToken();
    // Redirect to public homepage after logout
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, sessionToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
